'use client';

import React, { useRef, useEffect } from 'react';
import styles from './SleepFeatureCards.module.css';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import useEmblaCarousel from 'embla-carousel-react';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import { useMasterTimeline } from 'hooks/animation/useMasterTimeline';

const SleepFeatureCardsSection = React.forwardRef<
  HTMLElement,
  React.HTMLAttributes<HTMLElement>
>(({ children, ...props }, ref) => (
  <section ref={ref} className={styles.sleepFeatureCardsSection} {...props}>
    {children}
  </section>
));
SleepFeatureCardsSection.displayName = 'SleepFeatureCardsSection';

const StickyWrapper = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, ...props }, ref) => (
  <div ref={ref} className={styles.stickyWrapper} {...props}>
    {children}
  </div>
));
StickyWrapper.displayName = 'StickyWrapper';

const StickyContainer = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.stickyContainer}>{children}</div>
);

const ContentContainer = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.contentContainer}>{children}</div>
);

const TextWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.textWrapper}>{children}</div>
);

const TextContainer = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, ...props }, ref) => (
  <div ref={ref} className={styles.textContainer} {...props}>
    {children}
  </div>
));
TextContainer.displayName = 'TextContainer';

const Caption = ({ children }: { children: React.ReactNode }) => (
  <p className={styles.caption}>{children}</p>
);

const Title = ({ children }: { children: React.ReactNode }) => (
  <h2 className={styles.title}>{children}</h2>
);

const Description = ({ children }: { children: React.ReactNode }) => (
  <p className={styles.description}>{children}</p>
);

const CardsContainer = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.cardsContainer}>{children}</div>
);

const Card = React.forwardRef<
  HTMLDivElement,
  { $zIndex?: number } & React.HTMLAttributes<HTMLDivElement>
>(({ $zIndex, children, ...props }, ref) => (
  <div
    ref={ref}
    className={styles.card}
    style={{ ['--card-z']: $zIndex || 1 } as React.CSSProperties}
    {...props}
  >
    {children}
  </div>
));
Card.displayName = 'Card';

const CardImage = ({ src }: { src?: string }) => (
  <div
    className={styles.cardImage}
    style={
      src ? ({ ['--card-src']: `url(${src})` } as React.CSSProperties) : undefined
    }
  />
);

const CardOverlay = () => <div className={styles.cardOverlay} />;

const WhiteOverlay = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>((props, ref) => <div ref={ref} className={styles.whiteOverlay} {...props} />);
WhiteOverlay.displayName = 'WhiteOverlay';

const MobileCarouselContainer = ({
  children,
}: {
  children: React.ReactNode;
}) => <div className={styles.mobileCarouselContainer}>{children}</div>;

const MobileCarouselWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.mobileCarouselWrapper}>{children}</div>
);

const MobileEmbla = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, ...props }, ref) => (
  <div ref={ref} className={styles.mobileEmbla} {...props}>
    {children}
  </div>
));
MobileEmbla.displayName = 'MobileEmbla';

const MobileEmblaContainer = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.mobileEmblaContainer}>{children}</div>
);

const MobileEmblaSlide = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.mobileEmblaSlide}>{children}</div>
);

const MobileImageWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.mobileImageWrapper}>{children}</div>
);

const MobileImage = ({ src }: { src?: string }) => (
  <div
    className={styles.mobileImage}
    style={
      src
        ? ({ ['--mobile-src']: `url(${src})` } as React.CSSProperties)
        : undefined
    }
  />
);

const MobileTextContainer = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, ...props }, ref) => (
  <div ref={ref} className={styles.mobileTextContainer} {...props}>
    {children}
  </div>
));
MobileTextContainer.displayName = 'MobileTextContainer';

const MobileCaption = ({ children }: { children: React.ReactNode }) => (
  <p className={styles.mobileCaption}>{children}</p>
);

const MobileTitle = ({ children }: { children: React.ReactNode }) => (
  <h2 className={styles.mobileTitle}>{children}</h2>
);

const MobileSubtitle = ({ children }: { children: React.ReactNode }) => (
  <p className={styles.mobileSubtitle}>{children}</p>
);

const MobileTextWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.mobileTextWrapper}>{children}</div>
);

const SLEEP_FEATURE_FOLDS: Array<{
  caption: string;
  title: string;
  description: string;
  image?: string;
}> = [
  {
    caption: 'COMPREHENSIVE SLEEP REPORTS',
    title: 'Access a unified view of your sleep',
    description:
      'The Ambient Sleep Score brings together insights on sleep quality and sleep environment, helping you take informed decisions for better recovery.',
    image: getCompressedAssetUrl('/web_v2/uh-home-v2/cards/final/1.png'),
  },
  {
    caption: 'ENVIRONMENTAL CONTRIBUTORS',
    title: 'Understand what impacts your rest',
    description:
      'Monitors subtle shifts in your environment while you sleep. Ultrahuman Home tracks light, air freshness, temperature, and noise to help you sleep better.',
    image: getCompressedAssetUrl('/web_v2/uh-home-v2/cards/final/2-new.png'),
  },
  {
    caption: 'RESPIRATORY HEALTH SCORE',
    title: 'Decode your breathing patterns',
    description:
      'The respiratory health insights helps you analyze snoring, coughing and irregular breathing patterns to help catch early signs of potential respiratory issues.',
    image: getCompressedAssetUrl('/web_v2/uh-home-v2/cards/final/3.png'),
  },
];

const SleepFeatureCards = React.memo(() => {
  const sectionRef = useRef<HTMLElement>(null);
  const stickyWrapperRef = useRef<HTMLDivElement>(null);
  const textContainerRefs = useRef<HTMLDivElement[]>([]);
  const cardRefs = useRef<HTMLDivElement[]>([]);
  const whiteOverlayRefs = useRef<HTMLDivElement[]>([]);
  const gsapContext = useGsapContext();
  const { add } = useMasterTimeline();

  const mobileTextRefs = useRef<HTMLDivElement[]>([]);
  const { width } = useWindowSize();
  const isMobile = width <= 767;

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'center',
    slidesToScroll: 1,
    containScroll: 'trimSnaps',
    dragFree: false,
  });

  const ACTIVE_SCALE = 1;
  const SCALE_DECREMENT = 0.2; // Each card behind is 15% smaller
  const BASE_OFFSET_X = 23; // % of card width per position behind
  const BASE_OFFSET_Y = 0; // % of card height per position behind
  const BASE_OVERLAY_OPACITY = 0; // Base overlay opacity for first behind card
  const OVERLAY_OPACITY_INCREMENT = 0; // Additional opacity per position behind

  useIsomorphicLayoutEffect(() => {
    if (!stickyWrapperRef.current) return undefined;

    let refreshHandler: (() => void) | null = null;

    const numFolds = SLEEP_FEATURE_FOLDS.length;
    if (cardRefs.current.length !== numFolds) {
      cardRefs.current = new Array(numFolds).fill(null);
    }
    if (textContainerRefs.current.length !== numFolds) {
      textContainerRefs.current = new Array(numFolds).fill(null);
    }
    if (whiteOverlayRefs.current.length !== numFolds) {
      whiteOverlayRefs.current = new Array(numFolds).fill(null);
    }

    gsapContext.add(() => {
      const applyInitialStates = () => {
        cardRefs.current.forEach((card, index) => {
          if (!card) return;

          const cardWidth = card.offsetWidth || 453;
          const cardHeight = card.offsetHeight || 601;
          const whiteOverlay = whiteOverlayRefs.current[index];
          const positionBehind = index;
          const scale = Math.max(
            ACTIVE_SCALE - positionBehind * SCALE_DECREMENT,
            0.5,
          );
          const offsetXPercent = positionBehind * BASE_OFFSET_X;
          const offsetYPercent = positionBehind * BASE_OFFSET_Y;
          const overlayOpacity =
            positionBehind === 0
              ? 0
              : BASE_OVERLAY_OPACITY +
                (positionBehind - 1) * OVERLAY_OPACITY_INCREMENT;

          const offsetX = (offsetXPercent / 100) * cardWidth;
          const offsetY = (offsetYPercent / 100) * cardHeight;

          gsap.set(card, {
            scale,
            xPercent: -50 + (offsetX / cardWidth) * 100,
            yPercent: -50 + (offsetY / cardHeight) * 100,
            opacity: 1,
            y: 0,
          });

          if (whiteOverlay) {
            gsap.set(whiteOverlay, { opacity: overlayOpacity });
          }
        });

        textContainerRefs.current.forEach((text, index) => {
          if (!text) return;

          gsap.set(text, {
            opacity: index === 0 ? 1 : 0,
          });
        });
      };

      applyInitialStates();
      refreshHandler = applyInitialStates;
      ScrollTrigger.addEventListener('refreshInit', applyInitialStates);

      const tl = gsap.timeline({
        defaults: {
          duration: 1,
          ease: 'power1.out',
        },
        scrollTrigger: {
          immediateRender: false,
          trigger: stickyWrapperRef.current,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 1.5,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          refreshPriority: -1,
          onUpdate: (self) => {
            const progress = self.progress;
            const totalFolds = numFolds;

            const foldProgress = progress * (totalFolds - 1);
            const currentFoldIndex = Math.min(
              Math.floor(foldProgress),
              totalFolds - 1,
            );

            textContainerRefs.current.forEach((text, index) => {
              if (!text) return;

              const currentOpacity = gsap.getProperty(
                text,
                'opacity',
              ) as number;

              if (index < currentFoldIndex - 1 && currentOpacity > 0.01) {
                gsap.set(text, { opacity: 0, force3D: true });
              } else if (
                index > currentFoldIndex + 1 &&
                currentOpacity > 0.01
              ) {
                gsap.set(text, { opacity: 0, force3D: true });
              }
            });
          },
        },
      });
      add(tl, 'home-sleep-feature-cards', 2);

      cardRefs.current.forEach((card, index) => {
        if (!card) return;

        const cardWidth = card.offsetWidth || 453;
        const cardHeight = card.offsetHeight || 601;
        const whiteOverlay = whiteOverlayRefs.current[index];

        const positionBehind = index;
        const scale = Math.max(
          ACTIVE_SCALE - positionBehind * SCALE_DECREMENT,
          0.5,
        );
        const offsetXPercent = positionBehind * BASE_OFFSET_X;
        const offsetYPercent = positionBehind * BASE_OFFSET_Y;
        const overlayOpacity =
          positionBehind === 0
            ? 0
            : BASE_OVERLAY_OPACITY +
              (positionBehind - 1) * OVERLAY_OPACITY_INCREMENT;

        const offsetX = (offsetXPercent / 100) * cardWidth;
        const offsetY = (offsetYPercent / 100) * cardHeight;

        tl.set(
          card,
          {
            scale,
            xPercent: -50 + (offsetX / cardWidth) * 100,
            yPercent: -50 + (offsetY / cardHeight) * 100,
            opacity: 1,
          },
          '0',
        );

        if (whiteOverlay) {
          tl.set(whiteOverlay, { opacity: overlayOpacity }, '0');
        }
      });

      textContainerRefs.current.forEach((text, index) => {
        if (text) {
          gsap.set(text, {
            opacity: index === 0 ? 1 : 0,
            immediateRender: true,
          });
        }
      });

      tl.to({}, { duration: 0.3 }, '0');

      SLEEP_FEATURE_FOLDS.forEach((_, foldIndex) => {
        if (foldIndex === 0) return;

        const prevFoldIndex = foldIndex - 1;
        const currentCard = cardRefs.current[prevFoldIndex];
        const nextCard = cardRefs.current[foldIndex];
        const nextOverlay = whiteOverlayRefs.current[foldIndex];
        const prevText = textContainerRefs.current[prevFoldIndex];
        const nextText = textContainerRefs.current[foldIndex];

        const textTransitionStart = '-=0.15';
        const textTransitionDuration = 0.1;

        if (prevText) {
          tl.to(
            prevText,
            {
              opacity: 0,
              duration: textTransitionDuration,
              ease: 'power2.out',
              immediateRender: false,
            },
            textTransitionStart,
          );
        }

        if (nextText) {
          tl.fromTo(
            nextText,
            {
              opacity: 0,
              immediateRender: true,
            },
            {
              opacity: 1,
              duration: textTransitionDuration,
              ease: 'power2.out',
              immediateRender: false,
            },
            textTransitionStart,
          );
        }

        tl.to(
          currentCard,
          {
            y: -800,
            opacity: 0,
            duration: 0.25,
            ease: 'power1.in',
          },
          '-=0.15',
        );

        tl.to(
          nextCard,
          {
            scale: ACTIVE_SCALE,
            xPercent: -50,
            yPercent: -50,
            opacity: 1,
            duration: 0.3,
            ease: 'power1.out',
          },
          '-=0.18',
        );

        if (nextOverlay) {
          tl.to(
            nextOverlay,
            {
              opacity: 0,
              duration: 0.3,
              ease: 'power1.out',
            },
            '-=0.25',
          );
        }

        for (
          let behindIndex = foldIndex + 1;
          behindIndex < numFolds;
          behindIndex++
        ) {
          const behindCard = cardRefs.current[behindIndex];
          const behindOverlay = whiteOverlayRefs.current[behindIndex];
          if (!behindCard) continue;

          const cardWidth = behindCard.offsetWidth || 453;
          const cardHeight = behindCard.offsetHeight || 601;
          const newPositionBehind = behindIndex - foldIndex;
          const newScale = Math.max(
            ACTIVE_SCALE - newPositionBehind * SCALE_DECREMENT,
            0.5,
          );
          const newOffsetXPercent = newPositionBehind * BASE_OFFSET_X;
          const newOffsetYPercent = newPositionBehind * BASE_OFFSET_Y;
          const newOverlayOpacity =
            BASE_OVERLAY_OPACITY +
            (newPositionBehind - 1) * OVERLAY_OPACITY_INCREMENT;

          const offsetX = (newOffsetXPercent / 100) * cardWidth;
          const offsetY = (newOffsetYPercent / 100) * cardHeight;

          tl.to(
            behindCard,
            {
              scale: newScale,
              xPercent: -50 + (offsetX / cardWidth) * 100,
              yPercent: -50 + (offsetY / cardHeight) * 100,
              opacity: 1,
              duration: 0.3,
              ease: 'power1.out',
            },
            '-=0.25',
          );

          if (behindOverlay) {
            tl.to(
              behindOverlay,
              {
                opacity: newOverlayOpacity,
                duration: 0.3,
                ease: 'power1.out',
              },
              '-=0.25',
            );
          }
        }

        if (foldIndex < numFolds - 1) {
          tl.to({}, { duration: 0.3 }, '+=0.05');
        }
      });

      // Safari-specific: Delayed refresh to handle initial layout miscalculations
      const safariRefreshTimeout = setTimeout(() => {
        ScrollTrigger.refresh();
      }, 100);

      return () => {
        clearTimeout(safariRefreshTimeout);
        ScrollTrigger.removeEventListener('refreshInit', applyInitialStates);
      };
    });
    return () => {
      if (refreshHandler) {
        ScrollTrigger.removeEventListener('refreshInit', refreshHandler);
      }
    };
  }, [gsapContext]);

  useEffect(() => {
    if (!isMobile || !emblaApi) return;

    if (mobileTextRefs.current.length !== SLEEP_FEATURE_FOLDS.length) {
      mobileTextRefs.current = new Array(SLEEP_FEATURE_FOLDS.length).fill(null);
    }

    mobileTextRefs.current.forEach((textRef) => {
      if (textRef) {
        gsap.set(textRef, {
          opacity: 0,
        });
      }
    });

    const onSelect = () => {
      if (!emblaApi) return;
      const selectedIndex = emblaApi.selectedScrollSnap();

      mobileTextRefs.current.forEach((textRef, index) => {
        if (!textRef) return;

        if (
          index === selectedIndex &&
          selectedIndex >= 0 &&
          selectedIndex < SLEEP_FEATURE_FOLDS.length
        ) {
          gsap.to(textRef, {
            opacity: 1,
            duration: 0.3,
            ease: 'power2.inOut',
          });
        } else {
          gsap.to(textRef, {
            opacity: 0,
            duration: 0.3,
            ease: 'power2.inOut',
          });
        }
      });
    };

    emblaApi.on('select', onSelect);

    requestAnimationFrame(() => {
      emblaApi.scrollTo(0, false);
      onSelect();
    });

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, isMobile]);

  return (
    <SleepFeatureCardsSection ref={sectionRef} aria-label="Sleep Feature Cards">
      <StickyWrapper ref={stickyWrapperRef}>
        <StickyContainer>
          <ContentContainer>
            <TextWrapper>
              {SLEEP_FEATURE_FOLDS.map((fold, index) => (
                <TextContainer
                  key={`text-${index}`}
                  ref={(el) => {
                    if (el) textContainerRefs.current[index] = el;
                  }}
                >
                  <Caption>{fold.caption}</Caption>
                  <Title>{fold.title}</Title>
                  <Description>{fold.description}</Description>
                </TextContainer>
              ))}
            </TextWrapper>
            <CardsContainer>
              {SLEEP_FEATURE_FOLDS.map((fold, index) => (
                <Card
                  key={`card-${index}`}
                  $zIndex={SLEEP_FEATURE_FOLDS.length - index}
                  ref={(el) => {
                    if (el) cardRefs.current[index] = el;
                  }}
                >
                  <CardImage src={fold.image} />
                  <CardOverlay />
                  <WhiteOverlay
                    ref={(el) => {
                      if (el) whiteOverlayRefs.current[index] = el;
                    }}
                  />
                </Card>
              ))}
            </CardsContainer>
          </ContentContainer>
        </StickyContainer>
      </StickyWrapper>

      <MobileCarouselContainer>
        <MobileCarouselWrapper>
          <MobileEmbla ref={emblaRef}>
            <MobileEmblaContainer>
              {SLEEP_FEATURE_FOLDS.map((fold, index) => (
                <MobileEmblaSlide key={`mobile-slide-${index}`}>
                  <MobileImageWrapper>
                    <MobileImage src={fold.image} />
                  </MobileImageWrapper>
                </MobileEmblaSlide>
              ))}
            </MobileEmblaContainer>
          </MobileEmbla>
          <MobileTextWrapper>
            {SLEEP_FEATURE_FOLDS.map((fold, index) => (
              <MobileTextContainer
                key={`mobile-text-${index}`}
                ref={(el) => {
                  if (el) mobileTextRefs.current[index] = el;
                }}
              >
                <MobileCaption>{fold.caption}</MobileCaption>
                <MobileTitle>{fold.title}</MobileTitle>
                <MobileSubtitle>{fold.description}</MobileSubtitle>
              </MobileTextContainer>
            ))}
          </MobileTextWrapper>
        </MobileCarouselWrapper>
      </MobileCarouselContainer>
    </SleepFeatureCardsSection>
  );
});

SleepFeatureCards.displayName = 'SleepFeatureCards';

export default SleepFeatureCards;
